import React from 'react'
import PropTypes from 'prop-types'
import classNames from 'classnames'
import uniqueId from 'lodash/uniqueId'
import {
    Button,
    Icon,
    Tooltip,
} from 'react-wot-bootstrap'
import FamePoints from 'components/FamePoints/FamePoints'
import Back from 'components/Back/Back'
import WGCWXConfirmation from 'components/WGCWXConfirmation/WGCWXConfirmation'
import { getGroupsType, isCnRealm } from 'containers/WGCWXShop/helpers'

import { KEY_CODES, NATIONS_BY_REALM } from 'constants'
import { PRODUCTS_CONFIG } from 'containers/WGCWXShop/products.config'
import {
    PRODUCT_TYPES,
    GROUP_TYPES,
} from 'containers/WGCWXShop/constants'
import {
    AFFIRMATIVE,
    CURRENCY_TO_SPEND,
    CURRENCY_LEFT,
    NO,
    PURCHASE,
    BACK_TO_MAIN,
    STYLE_IS_IN_INVENTORY_ALREADY,
    GROUP_CAPTIONS,
    getTooltipTitleForGroup,
} from 'containers/WGCWXShop/translations'
import {
    ALL_BONUSES_PURCHASED,
    NOT_ENOUGH_PFP,
    NOT_ENOUGH_PFP_FOR_TANK_WARNING,
    PURCHASE_IS_NOT_AVAILABLE_YET,
    PURCHASE_PRODUCT,
    PURCHASE_PRODUCT_CONFIRMATION,
    PRODUCT_CONTENT,
    NATIONS_TITLE,
} from './translations'

import { ProductPropTypes, RewardPropTypes } from 'containers/WGCWXShop/propTypes'

import styles from './WGCWXShopDetailedProduct.scss'
import confirmationStyles from 'components/WGCWXConfirmation/WGCWXConfirmation.scss'
import FilterSelectButton from 'containers/WGCWXShop/components/FilterSelectButton/FilterSelectButton'

class WGCWXShopDetailedProduct extends React.Component {
    static propTypes = {
        accountFamePoints: PropTypes.number.isRequired,
        product: ProductPropTypes,
        tankProduct: PropTypes.arrayOf(RewardPropTypes),
        productType: PropTypes.string.isRequired,
        realm: PropTypes.string.isRequired,
        globalMapStylesInInventory: PropTypes.arrayOf(PropTypes.number).isRequired,

        purchaseItem: PropTypes.func.isRequired,
        close: PropTypes.func.isRequired,
    }

    static getTooltipGroupContent(group) {
        return (
            <p className={styles.tooltipSubtitle}>{getTooltipTitleForGroup(group)}</p>
        )
    }

    constructor(props) {
        super(props)

        this.productConfig = PRODUCTS_CONFIG[props.productType]

        this.state = {
            confirmationOpened: false,
            selectedGroup: this.selectFirstAvailableGroup(),
        }
        this.tooltipGroupId = uniqueId('group-tooltip-id-')
    }

    componentDidMount() {
        window.addEventListener('keydown', this.handleKeyPress)
    }

    componentWillUnmount() {
        window.removeEventListener('keydown', this.handleKeyPress)
    }

    get isAllGroupsAreBought() {
        if (getGroupsType(this.props.product) !== GROUP_TYPES.root) {
            const groupKeys = this.getGroupKeys()
            return groupKeys.every(group => this.groupIsBought(group))
        }
        return false
    }

    selectFirstAvailableGroup = () => {
        const groupsType = getGroupsType(this.props.product)
        if (groupsType !== GROUP_TYPES.root) {
            const groupKeys = groupsType === GROUP_TYPES.nation ?
                this.sortNationsByRealm() :
                this.getGroupKeys()
            return groupKeys.find(item => !this.groupIsBought(item)) || groupKeys[0]
        }
    }

    selectGroup = (selectedGroup) => {
        this.setState({ selectedGroup })
    }

    getGroups() {
        return Object.entries(this.props.product.groups)
    }

    getGroupKeys() {
        const groupKeys = Object.keys(this.props.product.groups)
        return groupKeys.map(item => item.split(':')[1])
    }

    getCurrentGroup() {
        const groups = this.getGroups()
        const currentGroup = groups.find(item => item[0].includes(this.state.selectedGroup)) || groups[0]
        return currentGroup[1]
    }

    getCurrentItem() {
        const currentGroup = this.getCurrentGroup()
        return currentGroup.find(item => !item.is_bought)
    }

    groupIsBought(groupName) {
        const groups = this.getGroups()
        const group = groups.find(item => item[0].includes(groupName))
        const groupValues = group[1]

        return !groupValues.find(item => !item.is_bought)
    }

    isStyleAlreadyPurchased = (product) => {
        if (!product.is_one_time_purchase) return
        const productToken = product.groups.root[0].token

        return this.props.globalMapStylesInInventory.includes(productToken)
    }

    sortNationsByRealm() {
        const realmNations = NATIONS_BY_REALM[this.props.realm]
        return realmNations.filter(item => this.getGroupKeys().includes(item))
    }

    translateGroup = (group) => {
        if (group) {
            return <span>{': '}{GROUP_CAPTIONS[group]}</span>
        }
    }

    handleKeyPress = e => {
        if (e.keyCode !== KEY_CODES.ESCAPE) return

        if (this.state.confirmationOpened) return

        this.props.close()
    }

    openConfirmation = () => {
        this.setState({ confirmationOpened: true })
    }

    closeConfirmation = () => {
        this.setState({ confirmationOpened: false })
    }

    purchase = () => {
        const currentItem = this.getCurrentItem()
        this.props.purchaseItem(currentItem.id)
    }

    showTankWarning = currentItem => {
        const tankItem = this.props.tankProduct && this.props.tankProduct[0]
        return (
            tankItem &&
            tankItem.price !== null &&
            tankItem.price <= this.props.accountFamePoints &&
            (this.props.accountFamePoints - currentItem.price) < tankItem.price &&
            !this.props.tankProduct.some(tankItem => tankItem.isSelected)
        )
    }

    renderFooterWithNotification(message) {
        return (
            <div className={styles.footer}>
                <footer>
                    <div className={styles.status}>
                        <Icon glyph="info" />
                        {message}
                    </div>
                    <div className={styles.action}>
                        <Button onClick={this.props.close}>{AFFIRMATIVE}</Button>
                    </div>
                </footer>
            </div>
        )
    }

    renderFooterWithPrice(isAllPurchased, currentItem) {
        const lackOfFP = !isAllPurchased && currentItem.price > this.props.accountFamePoints

        return (
            <div className={styles.footer}>
                <footer>
                    <div className={styles.status} />
                    <div className={styles.action}>
                        <span className={styles.price}>
                            <FamePoints value={currentItem.price} notAvailable={lackOfFP} />
                        </span>
                        <Button
                            isDisabled={lackOfFP}
                            tooltipId={lackOfFP ? this.props.productType : null}
                            onClick={this.openConfirmation}
                        >
                            {PURCHASE_PRODUCT}
                        </Button>
                        <Tooltip id={this.props.productType}>{NOT_ENOUGH_PFP}</Tooltip>
                    </div>
                </footer>
            </div>
        )
    }

    renderFooter(currentItem) {
        const isAllPurchased = this.props.accountFamePoints !== null && currentItem === undefined
        const isPurchaseDisabled = isAllPurchased || !this.props.product.is_available_to_buy

        if (isPurchaseDisabled) {
            const allBonusesPurchasedText = this.isAllGroupsAreBought ?
                ALL_BONUSES_PURCHASED[GROUP_TYPES.root] :
                ALL_BONUSES_PURCHASED[getGroupsType(this.props.product)]
            const allPurchasedText = isAllPurchased ? allBonusesPurchasedText : PURCHASE_IS_NOT_AVAILABLE_YET

            return this.renderFooterWithNotification(allPurchasedText)
        }

        if (this.isStyleAlreadyPurchased(this.props.product)) {
            return this.renderFooterWithNotification(STYLE_IS_IN_INVENTORY_ALREADY)
        }

        return this.renderFooterWithPrice(isAllPurchased, currentItem)
    }

    renderItem(item, isCurrent) {
        const itemClassNames = classNames(
            styles.list_item,
            item.is_bought && styles.list_item__bought,
            isCurrent && styles.list_item__current,
        )
        return (
            <li key={item.id} className={itemClassNames}>
                <span className={styles.list_item_title}>
                    {this.productConfig(item).shortDetailedTitle || this.productConfig(item).title}
                    {this.getGroups().length > 1 && this.translateGroup(this.state.selectedGroup)}
                </span>
                {
                    !item.is_bought &&
                    <span className={styles.pfp}>
                        <FamePoints size="small" value={item.price} inactive={!isCurrent} />
                    </span>
                }
            </li>
        )
    }

    renderGroupsButtons() {
        let groupKeys = this.getGroupKeys()

        if (getGroupsType(this.props.product) === GROUP_TYPES.nation) {
            groupKeys = this.sortNationsByRealm()
        }

        return groupKeys.map((groupName) => {
            const isChecked = this.state.selectedGroup === groupName
            const classNameGroup = classNames(
                styles.group,
                styles[`group__${groupName}`],
                isChecked && styles.isActive,
                isCnRealm(this.props.realm) && styles.isCnRealm,
            )
            const classNameButton = classNames(styles.button, {
                [styles.button__isBought]: this.groupIsBought(groupName),
            })
            return (
                <FilterSelectButton
                    key={`product-group-${groupName}`}
                    isChecked={isChecked}
                    caption={<span className={classNameGroup} />}
                    value={groupName}
                    onChange={this.selectGroup}
                    tooltipId={this.tooltipGroupId}
                    cssClassName={classNameButton}
                />
            )
        })
    }

    renderGroupsBlock() {
        return (
            <div className={styles.itemContent}>
                <span className={styles.itemContent_title}>
                    {NATIONS_TITLE}
                </span>
                <div className={styles.buttons}>
                    {this.renderGroupsButtons()}
                </div>
            </div>
        )
    }

    renderPriceList(currentGroup, currentItem) {
        return (
            <ul className={styles.list}>
                {currentGroup.map(item => this.renderItem(item, item === currentItem))}
            </ul>
        )
    }

    render() {
        const groups = this.getGroups()
        const currentGroup = this.getCurrentGroup()
        const currentItem = this.getCurrentItem()
        const groupsType = getGroupsType(this.props.product)
        const hasOneGroup = (groups.length === 1) && (groupsType !== GROUP_TYPES.root)
        const isNationalItem = (groupsType === GROUP_TYPES.nation)

        if (this.state.confirmationOpened) {
            const showTankWarning = this.showTankWarning(currentItem)
            const productTitle = this.productConfig(currentItem).title
            const groupName = this.translateGroup(this.state.selectedGroup)

            return (
                <WGCWXConfirmation
                    messageType={showTankWarning ? 'alert' : undefined}
                    title={PURCHASE_PRODUCT_CONFIRMATION(productTitle, groupName)}
                    buttonsOptions={[{
                        caption: PURCHASE,
                        callback: this.purchase,
                    }, {
                        caption: NO,
                        callback: this.closeConfirmation,
                    }]}
                    description={showTankWarning ? NOT_ENOUGH_PFP_FOR_TANK_WARNING : undefined}
                    onClose={this.closeConfirmation}
                >
                    <p className={confirmationStyles.price}>
                        {CURRENCY_TO_SPEND(
                            <FamePoints size="large" value={currentItem.price} />
                        )}
                        <span className={confirmationStyles.price_divider} />
                        {CURRENCY_LEFT(
                            <FamePoints size="large" value={this.props.accountFamePoints - currentItem.price} />
                        )}
                    </p>
                </WGCWXConfirmation>
            )
        }

        const titleItem = currentItem || currentGroup[currentGroup.length - 1]

        const imageClassName = classNames(
            styles.image,
            styles[`image__${this.props.productType}`],
            isCnRealm(this.props.realm) && styles.isCnRealm,
            this.props.productType === PRODUCT_TYPES.PREMIUM_PLUS &&
            styles[`image__${this.props.productType}_${titleItem.value}`],
            groupsType !== GROUP_TYPES.root &&
            styles[`image__${this.props.productType}_${this.state.selectedGroup}`],
        )

        const flagClassName = classNames(
            styles.flag,
            styles[`flag__${this.state.selectedGroup}`],
            isCnRealm(this.props.realm) && styles.isCnRealm,
        )

        const { title, shortDetailedTitle, description, content } = this.productConfig(titleItem)

        return (
            <div className={styles.base}>
                <div className={styles.holder}>
                    <div className={styles.back}>
                       <Back handleClick={this.props.close}>
                            <span className={styles.backTitle}>{BACK_TO_MAIN}</span>
                        </Back>
                    </div>
                    <h1 className={styles.header}>
                        {shortDetailedTitle || title}
                        {hasOneGroup && this.translateGroup(this.state.selectedGroup)}
                    </h1>
                    <div className={styles.content}>
                        <div className={styles.info}>
                            <p className={styles.description}>{description}</p>
                            {
                                content &&
                                <div className={styles.itemContent}>
                                    <span className={styles.itemContent_title}>
                                        {PRODUCT_CONTENT}
                                    </span>
                                    {content}
                                </div>
                            }
                            {
                                isNationalItem &&
                                this.renderGroupsBlock()
                            }
                            {
                                currentGroup.length > 1 &&
                                this.renderPriceList(currentGroup, currentItem)
                            }
                        </div>
                        <div className={styles.imageContainer}>
                            {isNationalItem && <div className={flagClassName} />}
                            <div className={imageClassName} />
                        </div>
                    </div>
                    {this.renderFooter(currentItem)}
                </div>
                <Tooltip id={this.tooltipGroupId} getContent={WGCWXShopDetailedProduct.getTooltipGroupContent}/>
            </div>
        )
    }
}

export default WGCWXShopDetailedProduct



// WEBPACK FOOTER //
// ./containers/WGCWXShop/containers/WGCWXShopDetailedProduct/WGCWXShopDetailedProduct.jsx